/**
 * SEOHead - Dynamic meta tags, Open Graph, Twitter Cards, and JSON-LD structured data
 */

import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  type: "website" | "article";
  article?: {
    publishedTime: string;
    author: string;
    section: string;
    tags: string[];
  };
  image?: string;
  imageAlt?: string;
}

export function SEOHead({
  title,
  description,
  canonical,
  type,
  article,
  image,
  imageAlt,
}: SEOHeadProps) {
  const siteUrl = import.meta.env.VITE_APP_URL || "https://jumpbean.app";
  const fullCanonical = `${siteUrl}${canonical}`;
  const ogImage = image ? `${siteUrl}${image}` : `${siteUrl}/og-default.png`;
  const fullTitle = `${title} | JumpBean`;

  // JSON-LD structured data
  const structuredData =
    type === "article" && article
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: title,
          description: description,
          image: ogImage,
          datePublished: article.publishedTime,
          author: {
            "@type": "Person",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "JumpBean",
            logo: {
              "@type": "ImageObject",
              url: `${siteUrl}/logo.png`,
            },
          },
          mainEntityOfPage: fullCanonical,
        }
      : {
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "JumpBean Blog",
          description: description,
          url: fullCanonical,
          publisher: {
            "@type": "Organization",
            name: "JumpBean",
          },
        };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:site_name" content="JumpBean" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article-specific meta tags */}
      {type === "article" && article && (
        <>
          <meta
            property="article:published_time"
            content={article.publishedTime}
          />
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={article.section} />
          {article.tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}
